import clsx from 'clsx';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from 'ui';
import type { IconName } from 'ui';

import { getUserIdFromStorage } from '../../../services/serviceConfig';
import { RADAR_ALERT_STATUS } from '../../../types/radar.constants';
import type { RadarAlertStatus } from '../../../types/radar.constants';
import './_alert-resolved-status.scss';

/**
 * AlertResolvedStatus - Resolved-state block shown in place of the alert action
 * buttons once an alert has been attended, dismissed or auto-resolved. Renders an
 * informative line naming the reviewer ("you" when the current user resolved it)
 * plus a disabled-looking gradient badge. Shared between the radar alert drawer
 * and the KC progress detail screen.
 */

type AlertResolvedStatusProps = {
  className?: string;
  reviewedById?: string | null;
  reviewedByName?: string | null;
  status: RadarAlertStatus;
};

function AlertResolvedStatus({
  className,
  reviewedById = null,
  reviewedByName = null,
  status,
}: AlertResolvedStatusProps): JSX.Element | null {
  const { t } = useTranslation(['chatbot']);

  const isAttended = status === RADAR_ALERT_STATUS.REVIEWED;
  const isDismissed = status === RADAR_ALERT_STATUS.DISMISSED;
  const isResolved = status === RADAR_ALERT_STATUS.RESOLVED;

  if (!isAttended && !isDismissed && !isResolved) return null;

  const isReviewedByMe = reviewedById != null && reviewedById === getUserIdFromStorage();

  let reviewerName = reviewedByName ?? '';
  if (isResolved) {
    reviewerName = t('chatbot:ai_analytics_student_conversation_alert_resolved_by');
  } else if (isReviewedByMe) {
    reviewerName = t('chatbot:radar_reviewed_by_you');
  }

  let infoText: string;
  let badgeLabel: string;
  let badgeIcon: IconName;

  if (isResolved) {
    infoText = t('chatbot:ai_analytics_student_conversation_alert_resolved_info');
    badgeLabel = t('chatbot:ai_analytics_student_conversation_alert_resolved_badge');
    badgeIcon = 'check-circle-fill-sm';
  } else if (isAttended) {
    infoText = t('chatbot:ai_analytics_student_conversation_alert_attended_by');
    badgeLabel = t('chatbot:ai_analytics_inappropriate_use_status_attended');
    badgeIcon = 'check-circle-fill-sm';
  } else {
    infoText = t('chatbot:ai_analytics_student_conversation_alert_dismissed_by');
    badgeLabel = t('chatbot:ai_analytics_student_conversation_alert_dismissed_badge');
    badgeIcon = 'status_error_fill';
  }

  return (
    <div className={clsx('alert-resolved-status', className)}>
      <p className="alert-resolved-status__info">
        {infoText} <strong>{reviewerName}</strong>
      </p>
      <div className="alert-resolved-status__badge">
        <Icon color="white" name={badgeIcon} size="md" />
        {badgeLabel}
      </div>
    </div>
  );
}

export default AlertResolvedStatus;
